import { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { useApi } from '../api/client';
import { useToast } from '../components/Toast';
import type { ApiError } from '../api/client';

/* ───────── Types ───────── */
interface Kpi {
  KpiId: number;
  Code: string;
  Title: string;
  Description: string;
  Category: string;
  IsActive: boolean;
  IsRecurring?: boolean;
  CreatedAt: string;
}
interface Employee {
  EmployeeId?: number;
  Upn: string;
  DisplayName: string;
  Department?: string;
  ManagerUpn?: string;
}
interface DirectReport {
  upn: string;
  displayName: string;
}
interface Period {
  PeriodId: number;
  Name: string;
  StartDate: string;
  EndDate: string;
  IsLocked: boolean;
}

const CATEGORY_COLORS: Record<string, string> = {
  Finance:            '#3b82f6',
  Leasing:            '#8b5cf6',
  Property:           '#06b6d4',
  Admin:              '#f59e0b',
  Marketing:          '#ec4899',
  Management:         '#10b981',
  Training:           '#6366f1',
  Sales:              '#ef4444',
  'Client Relations': '#14b8a6',
  General:            '#6b7280',
};

const PAGE_SIZE = 50;

export default function KpiManagementPage() {
  const { isAdmin, isManager, token } = useAuth();
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const canManageKpis = isAdmin || isManager;

  /* ── Tabs ── */
  const [activeTab, setActiveTab] = useState<'library' | 'assign'>('library');

  /* ── Library state ── */
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<'Title' | 'Category'>('Title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  /* ── Create KPI ── */
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKpi, setNewKpi] = useState({ code: '', title: '', description: '', category: 'General', is_recurring: false });

  /* ── Inline rename ── */
  const [editingKpiId, setEditingKpiId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  /* ── Assign state ── */
  const [selectedKpis, setSelectedKpis] = useState<Set<string>>(new Set()); // KPI codes
  const [assignCategory, setAssignCategory] = useState<string>('All');
  const [assignSearch, setAssignSearch] = useState('');
  const [assignPage, setAssignPage] = useState(0);
  const [targetEmployees, setTargetEmployees] = useState<string[]>([]);
  const [targetPeriod, setTargetPeriod] = useState('');
  const [assignWeight, setAssignWeight] = useState('1.0');

  /* ── Queries ── */
  const { data: kpisRaw, isLoading: kpisLoading } = useQuery({
    queryKey: ['kpis', !!token, canManageKpis],
    enabled: canManageKpis && !!token,
    queryFn: () => api.get('/kpis') as Promise<Kpi[]>,
  });

  const { data: employeesRaw } = useQuery({
    queryKey: ['kpi-employees', !!token, isAdmin, isManager],
    enabled: canManageKpis && !!token,
    queryFn: async () => {
      if (isAdmin) {
        return api.get('/employees') as Promise<Employee[]>;
      }
      const directs = await (api.get('/employees/directs') as Promise<DirectReport[]>);
      return directs.map(d => ({
        Upn: d.upn,
        DisplayName: d.displayName,
      }));
    },
  });

  const { data: periodsRaw } = useQuery({
    queryKey: ['periods', !!token],
    enabled: canManageKpis && !!token,
    queryFn: () => api.get('/periods') as Promise<Period[]>,
  });

  /* ── Derived: categories ── */
  const categories = useMemo(() => {
    if (!kpisRaw) return [];
    const counts = new Map<string, number>();
    kpisRaw.forEach(k => {
      if (!showInactive && !k.IsActive) return;
      const cat = k.Category || 'General';
      counts.set(cat, (counts.get(cat) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({ name, count }));
  }, [kpisRaw, showInactive]);

  const totalActive = useMemo(() => categories.reduce((s, c) => s + c.count, 0), [categories]);

  /* ── Derived: employees ── */
  const employees = useMemo(() => {
    if (!employeesRaw) return [];
    const seen = new Set<string>();
    return employeesRaw
      .filter(e => {
        const upn = e.Upn?.trim().toLowerCase();
        if (!upn || !upn.includes('@') || seen.has(upn)) return false;
        seen.add(upn);
        return true;
      })
      .sort((a, b) => (a.DisplayName || a.Upn).localeCompare(b.DisplayName || b.Upn));
  }, [employeesRaw]);

  const periods = useMemo(() => {
    if (!periodsRaw) return [];
    return [...periodsRaw].sort((a, b) => new Date(b.StartDate).getTime() - new Date(a.StartDate).getTime());
  }, [periodsRaw]);

  /* ── Filter + sort + paginate (library) ── */
  const filteredKpis = useMemo(() => {
    if (!kpisRaw) return [];
    let arr = kpisRaw.filter(k => showInactive || k.IsActive);
    if (activeCategory !== 'All') arr = arr.filter(k => (k.Category || 'General') === activeCategory);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      arr = arr.filter(k =>
        k.Title.toLowerCase().includes(q) ||
        (k.Description || '').toLowerCase().includes(q)
      );
    }
    arr.sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [kpisRaw, activeCategory, searchTerm, showInactive, sortField, sortDir]);

  const totalPages = Math.ceil(filteredKpis.length / PAGE_SIZE);
  const pageKpis = filteredKpis.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  /* ── Filter + paginate (assign tab) ── */
  const assignKpis = useMemo(() => {
    if (!kpisRaw) return [];
    let arr = kpisRaw.filter(k => k.IsActive);
    if (assignCategory !== 'All') arr = arr.filter(k => (k.Category || 'General') === assignCategory);
    if (assignSearch.trim()) {
      const q = assignSearch.toLowerCase();
      arr = arr.filter(k => k.Title.toLowerCase().includes(q));
    }
    arr.sort((a, b) => a.Title.localeCompare(b.Title));
    return arr;
  }, [kpisRaw, assignCategory, assignSearch]);

  const assignTotalPages = Math.ceil(assignKpis.length / PAGE_SIZE);
  const assignPageKpis = assignKpis.slice(assignPage * PAGE_SIZE, (assignPage + 1) * PAGE_SIZE);

  /* ── Mutations ── */
  const createMut = useMutation({
    mutationFn: (kpi: typeof newKpi) => api.post('/kpis', kpi),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpis'] });
      qc.invalidateQueries({ queryKey: ['admin', 'kpis'] });
      setShowCreateForm(false);
      setNewKpi({ code: '', title: '', description: '', category: 'General', is_recurring: false });
      toast.success('KPI created');
    },
    onError: (e: ApiError) => toast.error((e as any)?.body?.detail || 'Failed'),
  });

  const renameMut = useMutation({
    mutationFn: ({ kpiId, title }: { kpiId: number; title: string }) =>
      api.put(`/kpis/${kpiId}`, { title }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpis'] });
      qc.invalidateQueries({ queryKey: ['admin', 'kpis'] });
      setEditingKpiId(null);
      toast.success('KPI renamed');
    },
    onError: (e: ApiError) => toast.error((e as any)?.body?.detail || 'Rename failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (kpiId: number) => api.del(`/kpis/${kpiId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpis'] });
      qc.invalidateQueries({ queryKey: ['admin', 'kpis'] });
      setDeleteConfirmId(null);
      toast.success('KPI deactivated');
    },
    onError: (e: ApiError) => toast.error((e as any)?.body?.detail || 'Delete failed'),
  });


  const toggleRecurringMut = useMutation({
    mutationFn: ({ kpiId, is_recurring }: { kpiId: number; is_recurring: boolean }) =>
      api.put(`/kpis/${kpiId}`, { is_recurring }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpis'] });
      qc.invalidateQueries({ queryKey: ['admin', 'kpis'] });
      toast.success('Recurring flag updated');
    },
    onError: (e: ApiError) => toast.error((e as any)?.body?.detail || 'Failed to update recurring'),
  });

  const bulkAssignMut = useMutation({
    mutationFn: (payload: { employee_upns: string[]; kpi_codes: string[]; period_id: number; weight: number }) =>
      api.post('/kpis/assignments/bulk', payload),
    onSuccess: (data: any) => {
      toast.success(`Assigned: ${data.created} new, ${data.updated} updated${data.errors?.length ? `, ${data.errors.length} errors` : ''}`);
      setSelectedKpis(new Set());
    },
    onError: (e: ApiError) => toast.error((e as any)?.body?.detail || 'Bulk assign failed'),
  });

  /* ── Handlers ── */
  const toggleSort = useCallback((field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }, [sortField]);

  const toggleKpiSelection = useCallback((code: string) => {
    setSelectedKpis(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedKpis(prev => {
      const next = new Set(prev);
      const allSelected = assignPageKpis.every(k => prev.has(k.Code));
      assignPageKpis.forEach(k => allSelected ? next.delete(k.Code) : next.add(k.Code));
      return next;
    });
  }, [assignPageKpis]);

  const selectAllInCategory = useCallback(() => {
    setSelectedKpis(prev => {
      const next = new Set(prev);
      const all = assignKpis.map(k => k.Code);
      const allSelected = all.every(c => prev.has(c));
      all.forEach(c => allSelected ? next.delete(c) : next.add(c));
      return next;
    });
  }, [assignKpis]);

  const handleBulkAssign = () => {
    if (selectedKpis.size === 0) { toast.warning('Select at least one KPI'); return; }
    if (targetEmployees.length === 0) { toast.warning('Select at least one employee'); return; }
    if (!targetPeriod) { toast.warning('Select a period'); return; }
    bulkAssignMut.mutate({
      employee_upns: targetEmployees,
      kpi_codes: Array.from(selectedKpis),
      period_id: Number(targetPeriod),
      weight: Number(assignWeight) || 1.0,
    });
  };

  /* ── Guard ── */
  if (!canManageKpis) return <div className="kpi-management"><h1>KPI Management</h1><p>Manager or admin access required.</p></div>;
  if (kpisLoading) return <div className="kpi-management"><div className="logo-loader-wrap"><img src="/logo.jpeg" alt="Loading" className="logo-spin" style={{ width: 80 }} /><p className="loader-text">Loading KPIs...</p></div></div>;

  /* ── Render ── */

  const csvEscape = (value: unknown) => {
    const raw = value == null ? '' : String(value);
    if (/[",\n\r]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
    return raw;
  };

  const downloadLibraryCsv = () => {
    if (filteredKpis.length === 0) {
      window.alert('No KPIs to download for the current filters.');
      return;
    }
    const headers = ['Code', 'Title', 'Category', 'Description', 'IsActive', 'IsRecurring'];
    const lines = [headers.join(',')];
    for (const k of filteredKpis) {
      lines.push([
        csvEscape(k.Code),
        csvEscape(k.Title),
        csvEscape(k.Category),
        csvEscape(k.Description),
        csvEscape(k.IsActive ? 'Yes' : 'No'),
        csvEscape(k.IsRecurring ? 'Yes' : 'No'),
      ].join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kpi-library-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };


  return (
    <div className="kpi-management" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>KPI Management</h1>
      <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 14 }}>
        {totalActive.toLocaleString()} active KPIs across {categories.length} categories
      </p>

      {/* ── Tabs ── */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={activeTab === 'library' ? 'active' : ''} onClick={() => setActiveTab('library')}>
          KPI Library
        </button>
        <button className={activeTab === 'assign' ? 'active' : ''} onClick={() => setActiveTab('assign')}>
          Assign KPIs {selectedKpis.size > 0 && <span style={{ background: '#3b82f6', color: '#fff', borderRadius: 99, padding: '2px 8px', fontSize: 12, marginLeft: 6 }}>{selectedKpis.size}</span>}
        </button>
      </div>

      {/* ═══════  LIBRARY TAB  ═══════ */}
      {activeTab === 'library' && (
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Sidebar: Categories */}
          <div style={{ minWidth: 200, flexShrink: 0 }}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', marginBottom: 8 }}>Categories</h3>
            <div
              onClick={() => { setActiveCategory('All'); setPage(0); }}
              style={{
                padding: '8px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, fontSize: 14,
                background: activeCategory === 'All' ? '#eff6ff' : 'transparent',
                fontWeight: activeCategory === 'All' ? 600 : 400,
                color: activeCategory === 'All' ? '#2563eb' : '#374151',
              }}
            >
              All <span style={{ float: 'right', color: '#9ca3af' }}>{totalActive}</span>
            </div>
            {categories.map(c => (
              <div
                key={c.name}
                onClick={() => { setActiveCategory(c.name); setPage(0); }}
                style={{
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: activeCategory === c.name ? '#eff6ff' : 'transparent',
                  fontWeight: activeCategory === c.name ? 600 : 400,
                  color: activeCategory === c.name ? '#2563eb' : '#374151',
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: 99, background: CATEGORY_COLORS[c.name] || '#6b7280', flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: '#9ca3af', fontSize: 13 }}>{c.count}</span>
              </div>
            ))}
          </div>

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Search + Create */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                placeholder="Search KPIs by title or description..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
                style={{ flex: 1, padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none' }}
              />
              <button
                onClick={downloadLibraryCsv}
                style={{ whiteSpace: 'nowrap', background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}
                title="Download filtered KPI library as CSV"
              >
                ⬇ Download
              </button>
              <button onClick={() => setShowCreateForm(true)} style={{ whiteSpace: 'nowrap' }}>
                + New KPI
              </button>
            </div>

            {/* Create form */}
            {showCreateForm && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px' }}>Create New KPI</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input placeholder="Title" value={newKpi.title} onChange={e => setNewKpi({ ...newKpi, title: e.target.value })} style={inputStyle} />
                  <select value={newKpi.category} onChange={e => setNewKpi({ ...newKpi, category: e.target.value })} style={inputStyle}>
                    {['General', 'Finance', 'Leasing', 'Property', 'Admin', 'Marketing', 'Management', 'Training', 'Sales', 'Client Relations'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <div />
                  <textarea placeholder="Description" value={newKpi.description} onChange={e => setNewKpi({ ...newKpi, description: e.target.value })} rows={2} style={{ ...inputStyle, gridColumn: '1 / -1' }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!newKpi.is_recurring}
                    onChange={e => setNewKpi({ ...newKpi, is_recurring: e.target.checked })}
                  />
                  Recurring <span style={{ color: '#9ca3af', fontSize: 12 }}>(roll into next month automatically)</span>
                </label>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button onClick={() => createMut.mutate(newKpi)}>Create</button>
                  <button onClick={() => setShowCreateForm(false)} style={{ background: '#e5e7eb', color: '#374151' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Results count */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13, color: '#6b7280' }}>
              <span>{filteredKpis.length.toLocaleString()} KPIs {searchTerm && `matching "${searchTerm}"`}</span>
            </div>

            {/* Table */}
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('Title')}>
                    Title {sortField === 'Title' && (sortDir === 'asc' ? '▲' : '▼')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('Category')}>
                    Category {sortField === 'Category' && (sortDir === 'asc' ? '▲' : '▼')}
                  </th>
                  {isAdmin && <th style={{ width: 180 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pageKpis.map(kpi => (
                  <tr key={kpi.KpiId}>
                    <td>
                      {editingKpiId === kpi.KpiId ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input
                            value={editTitle}
                            onChange={e => setEditTitle(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && editTitle.trim()) renameMut.mutate({ kpiId: kpi.KpiId, title: editTitle.trim() });
                              if (e.key === 'Escape') setEditingKpiId(null);
                            }}
                            autoFocus
                            style={{ flex: 1, padding: '4px 8px', fontSize: 13, border: '1px solid #3b82f6', borderRadius: 4, outline: 'none' }}
                          />
                          <button
                            onClick={() => editTitle.trim() && renameMut.mutate({ kpiId: kpi.KpiId, title: editTitle.trim() })}
                            disabled={renameMut.isPending}
                            style={{ padding: '3px 8px', fontSize: 12, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                          >✓</button>
                          <button
                            onClick={() => setEditingKpiId(null)}
                            style={{ padding: '3px 8px', fontSize: 12, background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                          >✕</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ fontWeight: 500 }}>{kpi.Title}</div>
                          {kpi.IsRecurring && (
                            <span style={{
                              padding: '1px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                              background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
                              letterSpacing: '0.03em', textTransform: 'uppercase',
                            }}>Recurring</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                        background: (CATEGORY_COLORS[kpi.Category] || '#6b7280') + '18',
                        color: CATEGORY_COLORS[kpi.Category] || '#6b7280',
                      }}>
                        {kpi.Category || 'General'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td>
                        {deleteConfirmId === kpi.KpiId ? (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: '#b91c1c' }}>Delete?</span>
                            <button
                              onClick={() => deleteMut.mutate(kpi.KpiId)}
                              disabled={deleteMut.isPending}
                              style={{ padding: '3px 8px', fontSize: 12, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                            >Yes</button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              style={{ padding: '3px 8px', fontSize: 12, background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                            >No</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button
                              onClick={() => { setEditingKpiId(kpi.KpiId); setEditTitle(kpi.Title); }}
                              style={{ padding: '3px 10px', fontSize: 12, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 4, cursor: 'pointer' }}
                            >Rename</button>
                            <button
                              onClick={() => toggleRecurringMut.mutate({ kpiId: kpi.KpiId, is_recurring: !kpi.IsRecurring })}
                              disabled={toggleRecurringMut.isPending}
                              style={{
                                padding: '3px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                                background: kpi.IsRecurring ? '#ecfdf5' : '#f9fafb',
                                color: kpi.IsRecurring ? '#047857' : '#6b7280',
                                border: kpi.IsRecurring ? '1px solid #a7f3d0' : '1px solid #d1d5db',
                              }}
                              title="Toggle monthly roll-forward"
                            >{kpi.IsRecurring ? 'Recurring on' : 'Make recurring'}</button>
                            <button
                              onClick={() => setDeleteConfirmId(kpi.KpiId)}
                              style={{ padding: '3px 10px', fontSize: 12, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer' }}
                            >Delete</button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', fontSize: 13 }}>← Prev</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page + 1} of {totalPages}</span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', fontSize: 13 }}>Next →</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════  ASSIGN TAB  ═══════ */}
      {activeTab === 'assign' && (
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Left: KPI selector */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {/* Category filter chips */}
              <button
                onClick={() => { setAssignCategory('All'); setAssignPage(0); }}
                style={{
                  padding: '4px 12px', borderRadius: 99, fontSize: 12, border: '1px solid #d1d5db', cursor: 'pointer',
                  background: assignCategory === 'All' ? '#2563eb' : '#fff',
                  color: assignCategory === 'All' ? '#fff' : '#374151',
                }}
              >
                All ({totalActive})
              </button>
              {categories.map(c => (
                <button
                  key={c.name}
                  onClick={() => { setAssignCategory(c.name); setAssignPage(0); }}
                  style={{
                    padding: '4px 12px', borderRadius: 99, fontSize: 12, border: '1px solid #d1d5db', cursor: 'pointer',
                    background: assignCategory === c.name ? (CATEGORY_COLORS[c.name] || '#6b7280') : '#fff',
                    color: assignCategory === c.name ? '#fff' : '#374151',
                  }}
                >
                  {c.name} ({c.count})
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                placeholder="Search KPIs..."
                value={assignSearch}
                onChange={e => { setAssignSearch(e.target.value); setAssignPage(0); }}
                style={{ flex: 1, padding: '8px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8 }}
              />
              <button onClick={selectAllOnPage} style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}>
                {assignPageKpis.every(k => selectedKpis.has(k.Code)) ? 'Deselect page' : 'Select page'}
              </button>
              <button onClick={selectAllInCategory} style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}>
                {assignKpis.every(k => selectedKpis.has(k.Code)) ? `Deselect all (${assignKpis.length})` : `Select all (${assignKpis.length})`}
              </button>
            </div>

            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
              {selectedKpis.size} KPI{selectedKpis.size !== 1 ? 's' : ''} selected · {assignKpis.length} shown
            </div>

            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={assignPageKpis.length > 0 && assignPageKpis.every(k => selectedKpis.has(k.Code))}
                      onChange={selectAllOnPage}
                    />
                  </th>
                  <th>Title</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {assignPageKpis.map(kpi => (
                  <tr
                    key={kpi.KpiId}
                    onClick={() => toggleKpiSelection(kpi.Code)}
                    style={{
                      cursor: 'pointer',
                      background: selectedKpis.has(kpi.Code) ? '#eff6ff' : undefined,
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedKpis.has(kpi.Code)}
                        onChange={() => toggleKpiSelection(kpi.Code)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <span>{kpi.Title}</span>
                      {kpi.IsRecurring && (
                        <span style={{
                          marginLeft: 8, padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                          background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
                          textTransform: 'uppercase',
                        }}>Recurring</span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                        background: (CATEGORY_COLORS[kpi.Category] || '#6b7280') + '18',
                        color: CATEGORY_COLORS[kpi.Category] || '#6b7280',
                      }}>
                        {kpi.Category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {assignTotalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <button disabled={assignPage === 0} onClick={() => setAssignPage(p => p - 1)} style={{ padding: '6px 14px', fontSize: 13 }}>← Prev</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Page {assignPage + 1} of {assignTotalPages}</span>
                <button disabled={assignPage >= assignTotalPages - 1} onClick={() => setAssignPage(p => p + 1)} style={{ padding: '6px 14px', fontSize: 13 }}>Next →</button>
              </div>
            )}
          </div>

          {/* Right: Assign panel (sticky) */}
          <div style={{
            width: 320, flexShrink: 0, position: 'sticky', top: 80, alignSelf: 'flex-start',
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20,
          }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Assign to Employees</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
              {selectedKpis.size} KPI{selectedKpis.size !== 1 ? 's' : ''} selected
            </p>

            {/* Multi-select employees */}
            <label style={labelStyle}>Employees</label>
            <select
              multiple
              value={targetEmployees}
              onChange={e => setTargetEmployees(Array.from(e.target.selectedOptions, o => o.value))}
              style={{ ...inputStyle, height: 120 }}
            >
              {employees.map(emp => (
                <option key={emp.Upn} value={emp.Upn}>
                  {emp.DisplayName || emp.Upn}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, marginBottom: 12 }}>
              Hold Ctrl/Cmd to select multiple
            </div>

            <label style={labelStyle}>Period</label>
            <select
              value={targetPeriod}
              onChange={e => setTargetPeriod(e.target.value)}
              style={{ ...inputStyle, marginBottom: 12 }}
            >
              <option value="">Select Period</option>
              {periods.map(p => (
                <option key={p.PeriodId} value={p.PeriodId}>
                  {p.Name} ({p.StartDate} → {p.EndDate})
                </option>
              ))}
            </select>

            <label style={labelStyle}>Weight</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={assignWeight}
              onChange={e => setAssignWeight(e.target.value)}
              style={{ ...inputStyle, marginBottom: 16 }}
            />

            <button
              onClick={handleBulkAssign}
              disabled={bulkAssignMut.isPending || selectedKpis.size === 0 || targetEmployees.length === 0 || !targetPeriod}
              style={{
                width: '100%', padding: '12px', fontSize: 14, fontWeight: 600,
                background: selectedKpis.size > 0 && targetEmployees.length > 0 && targetPeriod ? '#2563eb' : '#d1d5db',
                color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
              }}
            >
              {bulkAssignMut.isPending ? 'Assigning...' : `Assign ${selectedKpis.size} KPIs to ${targetEmployees.length} employee${targetEmployees.length !== 1 ? 's' : ''}`}
            </button>

            {selectedKpis.size > 0 && (
              <button
                onClick={() => setSelectedKpis(new Set())}
                style={{ width: '100%', marginTop: 8, padding: '8px', fontSize: 13, background: 'transparent', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', color: '#6b7280' }}
              >
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4,
};
